import { useCallback, useEffect, useState } from 'react';
import {
  surveyDaysUntil,
  type PanelPostEventRowDto,
  type PanelPostEventSurveyState,
} from '@icebreaker/shared';
import { Button, EmptyState, Skeleton, Table, Tag, type TagVariant } from '@icebreaker/ui';
import { subSurfacePermission } from '@/app/screen-permissions';
import { PermissionSurfaceGate } from '@/features/access/ui/permission-gate';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  fetchOrganizationPostEvent,
  surveyDisableReset,
} from '../store/organization-post-event.slice';
import { CloseSurveyDialog } from './close-survey-dialog';
import styles from './organization-post-event.module.css';

const SURVEY_TAG: Readonly<Record<PanelPostEventSurveyState, TagVariant>> = {
  none: 'neutral',
  draft: 'neutral',
  open: 'accent',
  closed: 'neutral',
  disabled_by_staff: 'warning',
};

/**
 * KURUM DETAYI › ETKİNLİK SONRASI (belge ve anket Story 8, tasarım kare 11).
 *
 * Bitmiş etkinlik başına satır: ad, bitiş tarihi (etkinliğin diliminde), belge açık
 * mı, anket durumu, cevap sayısı. Cevap içeriği ve kişi verisi yoktur; sayı sunucuda
 * 5'in katına yuvarlanmış gelir ve istemci "5'ten az"ın altını TAHMİN ETMEZ.
 * Dört hâl: dolu tablo, kesik çerçeveli boş durum, iskelet, satır içi hata.
 *
 * "Anketi kapat" yalnız AÇIK ankette ve kendi kapısıyla (`staff:moderate`) çizilir:
 * bölümü görmek kapatmaya yetmez, yetkisi olmayan personelde düğme DOM'a girmez.
 */
export function OrganizationPostEventSection({ organizationId }: { organizationId: string }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.panelOrgPostEvent);
  const [closing, setClosing] = useState<PanelPostEventRowDto | null>(null);
  const [closedTitle, setClosedTitle] = useState<string | null>(null);

  const load = useCallback(() => {
    dispatch(fetchOrganizationPostEvent.request({ organizationId }));
  }, [dispatch, organizationId]);

  useEffect(() => {
    load();
    dispatch(surveyDisableReset());
    setClosing(null);
    setClosedTitle(null);
  }, [dispatch, load]);

  const own = state.organizationId === organizationId ? state.data : null;
  const min = own?.minCount ?? 0;

  const onClosed = useCallback((title: string | null) => {
    setClosing(null);
    setClosedTitle(title);
  }, []);

  return (
    <section className={styles.section} aria-labelledby="organization-post-event-title">
      <h2 id="organization-post-event-title" className={styles.title}>
        {t.t('admin.orgs.postEvent.title')}
      </h2>
      {own === null && state.phase === 'failed' ? (
        <div className={styles.state} role="alert">
          <p className={styles.error}>{t.t('admin.orgs.postEvent.loadError')}</p>
          <Button variant="secondary" size="sm" onClick={load}>
            {t.t('admin.orgs.postEvent.retry')}
          </Button>
        </div>
      ) : own === null ? (
        <div className={styles.loading}>
          <Skeleton label={t.t('admin.orgs.postEvent.loading')} lines={4} />
        </div>
      ) : own.items.length === 0 ? (
        <EmptyState
          frame="dashed"
          title={t.t('admin.orgs.postEvent.emptyTitle')}
          body={t.t('admin.orgs.postEvent.emptyBody')}
        />
      ) : (
        <>
          {closedTitle !== null ? (
            <p className={styles.notice} role="status">
              {t.t('admin.orgs.postEvent.closedNotice', { eventTitle: closedTitle })}
            </p>
          ) : null}
          <div className={[styles.table, 'ib-priority-table'].join(' ')}>
            <Table aria-label={t.t('admin.orgs.postEvent.title')}>
              <thead>
                <tr>
                  <th>{t.t('admin.orgs.postEvent.colEvent')}</th>
                  <th>{t.t('admin.orgs.postEvent.colDate')}</th>
                  <th>{t.t('admin.orgs.postEvent.colCertificate')}</th>
                  <th>{t.t('admin.orgs.postEvent.colSurvey')}</th>
                  <th>{t.t('admin.orgs.postEvent.colResponses')}</th>
                  <th>
                    <span className={styles.srOnly}>{t.t('admin.orgs.postEvent.colAction')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {own.items.map((row) => (
                  <PostEventRow key={row.eventId} row={row} min={min} onClose={setClosing} />
                ))}
              </tbody>
            </Table>
          </div>
          <p className={styles.note}>{t.t('admin.orgs.postEvent.note', { min })}</p>
        </>
      )}
      {closing !== null ? (
        <CloseSurveyDialog
          organizationId={organizationId}
          eventId={closing.eventId}
          eventTitle={closing.title}
          onCancel={() => setClosing(null)}
          onClosed={onClosed}
        />
      ) : null}
    </section>
  );
}

function PostEventRow({
  row,
  min,
  onClose,
}: {
  row: PanelPostEventRowDto;
  min: number;
  onClose: (row: PanelPostEventRowDto) => void;
}) {
  const t = useTranslation();
  const survey =
    row.survey === 'open' && row.surveyClosesAt !== null
      ? t.t('admin.orgs.postEvent.surveyOpen', {
          count: surveyDaysUntil(row.surveyClosesAt, Date.now()),
        })
      : t.t(`admin.orgs.postEvent.survey.${row.survey}`);
  const responses =
    row.responseCount === null
      ? null
      : row.responseCount === 0
        ? t.t('admin.orgs.postEvent.fewerThanMin', { min })
        : t.formatNumber(row.responseCount);
  return (
    <tr>
      <td className={styles.eventCell}>{row.title}</td>
      <td className={styles.dateCell}>
        {t.formatDate(row.endsAt, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: row.timezone,
        })}
      </td>
      <td className={row.certificateEnabled ? undefined : styles.muted}>
        {t.t(
          row.certificateEnabled
            ? 'admin.orgs.postEvent.certificateOn'
            : 'admin.orgs.postEvent.certificateOff',
        )}
      </td>
      <td>
        <Tag
          variant={SURVEY_TAG[row.survey]}
          className={[styles.statusTag, row.survey === 'none' ? styles.muted : '']
            .filter(Boolean)
            .join(' ')}
        >
          {survey}
        </Tag>
      </td>
      <td className={[styles.numberCell, responses === null ? styles.muted : ''].join(' ')}>
        {responses ?? (
          <>
            <span aria-hidden="true">—</span>
            <span className={styles.srOnly}>{t.t('admin.orgs.postEvent.noResponses')}</span>
          </>
        )}
      </td>
      <td className={styles.actionCell}>
        {row.survey === 'open' ? (
          <PermissionSurfaceGate
            permission={subSurfacePermission('panel:event-survey-disable')}
            fallback={null}
          >
            <Button
              variant="rowNegative"
              size="sm"
              aria-label={t.t('admin.orgs.postEvent.closeAria', { eventTitle: row.title })}
              onClick={() => onClose(row)}
            >
              {t.t('admin.orgs.postEvent.close')}
            </Button>
          </PermissionSurfaceGate>
        ) : null}
      </td>
    </tr>
  );
}
